import { type Faq, type PhoneScript, type VoiceSettingsConfig } from "@/types/campaigns";
import { type EntityMutationEvent } from "@/types/liveReflection";
import { UseCase, UseCaseAssociation } from "@/types/scripts";
import { apiLive } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";

// IO + formatting helpers for useScriptLiveReflection. The pure reconcile
// algorithm lives in ./liveReflection.

export async function fetchUseCasesBatch(ids: string[], silent = false): Promise<Map<string, UseCase>> {
  const map = new Map<string, UseCase>();
  if (ids.length === 0) return map;

  const params = new URLSearchParams();
  ids.forEach((id) => params.append("useCaseIds", id));
  const response = await apiLive<UseCase[]>(
    `${GAIL_API}/v1/use_cases/batch?${params.toString()}`,
    undefined,
    undefined,
    undefined,
    silent
  );
  for (const useCase of response ?? []) {
    map.set(useCase.id, useCase);
  }
  return map;
}

export const serializeAssociations = (
  associations: UseCaseAssociation[] | undefined,
  names: Record<string, string>
): string =>
  [...(associations ?? [])]
    .filter((a) => a.status === "active")
    .sort((a, b) => a.order - b.order)
    .map((a) => names[a.id] ?? a.id)
    .join("\n");

export const serializeFaqs = (faqs: Faq[] | undefined): string => {
  if (!faqs || faqs.length === 0) return "";
  return faqs.map((f) => `Q: ${f.question}\nA: ${f.answer}`).join("\n\n");
};

export const serializeVoice = (script: Partial<PhoneScript>): string => {
  const parts = [
    script.voice ?? "",
    script.voiceProvider ?? "",
    serializeVoiceSettings(script.voiceSettings),
  ];
  return parts.filter(Boolean).join(" · ");
};

const serializeVoiceSettings = (settings: VoiceSettingsConfig | undefined): string => {
  if (!settings) return "";
  return JSON.stringify(settings);
};

export const parseEventTime = (events: readonly EntityMutationEvent[]): number => {
  const times = events
    .map((e) => (e.updated_at ? Date.parse(e.updated_at) : Number.NaN))
    .filter((t) => !Number.isNaN(t));
  return times.length > 0 ? Math.max(...times) : Date.now();
};
